/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// tanstack
import { useQuery } from '@tanstack/react-query';

// types
import { TParamsGetApi } from '@global-types/index';

// keys
import { queryKeys } from '@config/constants/query-keys';

// lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';
import { formatQueryParams } from '@utils/functions';
import { TProduction } from '../types';
import { TProductionDTO } from '../types/dto';
import { mapProduction } from '../utils/production-mapper';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponseGetProductions = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TProductionDTO[];
};

export type TFormattedProductionResponse = Omit<
	TApiResponseGetProductions,
	'rows'
> & {
	rows: TProduction[];
};

type TUseProductionsV2Options = {
	page: number;
	size: number;
	queryParams?: Record<string, any>;
	sortParams?: string;
	config?: QueryConfig<typeof getProductions>;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getProductions = (
	params: TParamsGetApi
): Promise<TApiResponseGetProductions> => {
	return apiClient.get('/productions', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: [
				'project',
				'site',
				'conversionFactor',
				'entity',
				'organization',
			],
			excludeFields: ['verifiedBy'],
			sort: params.sortParams
				? `${params.sortParams} _id`
				: '_id',
			query: {
				...formatQueryParams(params.queryParams),
			},
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hooks                                  */
/* -------------------------------------------------------------------------- */
export const useProductionsV2 = ({
	page,
	size,
	queryParams,
	sortParams,
	config,
}: TUseProductionsV2Options) => {
	return useQuery({
		...config,

		queryKey: [
			queryKeys.app.productions.list.key,
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],

		queryFn: () =>
			getProductions({
				page,
				pageSize: size,
				sortParams: sortParams ?? null,
				queryParams: queryParams ?? null,
			}),
		select: (data): TFormattedProductionResponse => {
			return {
				...data,
				rows: data.rows.map(mapProduction),
			};
		},
	});
};
